import { useState, useContext, useEffect } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Target, RotateCw, History, Volume2, VolumeX, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin, soundEffects } from "@/lib/celebrations";

export const LimboPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [targetMultiplier, setTargetMultiplier] = useState(2.0);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  useEffect(() => {
    soundEffects.setEnabled(soundEnabled);
  }, [soundEnabled]);

  const winChance = ((0.99 / targetMultiplier) * 100).toFixed(2);

  const playGame = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    setLoading(true);
    soundEffects.playSpin();

    try {
      const response = await axios.post(`${API}/games/limbo/play`, {
        game_type: "limbo",
        bet_amount: betAmount,
        bet_data: { target: targetMultiplier }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      setLastResult(response.data);
      setHistory(prev => [response.data, ...prev.slice(0, 9)]);

      if (response.data.won) {
        toast.success(`Won! Result: ${response.data.result}x >= Target: ${targetMultiplier}x | +₹${response.data.winnings.toFixed(2)}`);
        celebrateWin(response.data.multiplier, response.data.winnings);
      } else {
        toast.error(`Lost! Result: ${response.data.result}x < Target: ${targetMultiplier}x`);
        soundEffects.playLoss();
      }

      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#007AFF]/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="limbo-title">Limbo</h1>
              <p className="text-[#A1A1AA] text-sm">Set a target multiplier and beat it!</p>
            </div>
          </div>
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Result Display */}
          <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10 flex flex-col items-center justify-center min-h-[300px]">
            {lastResult ? (
              <div className="text-center">
                <p className="text-[#A1A1AA] text-sm mb-2">Result</p>
                <p className={`text-7xl font-bold font-mono ${
                  lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'
                }`}>
                  {lastResult.result}x
                </p>
                <p className="text-[#A1A1AA] mt-4">
                  Target: <span className="text-white">{lastResult.target}x</span>
                </p>
                {lastResult.won && (
                  <p className="text-[#00E701] text-2xl mt-2 font-bold">
                    +₹{lastResult.winnings.toFixed(2)}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center">
                <Target className="w-16 h-16 text-[#007AFF] mx-auto mb-4 opacity-50" />
                <p className="text-[#52525B]">Place a bet to play</p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {/* Bet Amount */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount</label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                disabled={loading}
                className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                data-testid="bet-input"
              />
              <div className="flex gap-2 mt-2">
                {[50, 100, 500, 1000].map(amt => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setBetAmount(amt)}
                    disabled={loading}
                    className="flex-1 border-white/10 text-white/60 hover:text-white text-xs"
                  >
                    ₹{amt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Target Multiplier */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <label className="text-[#A1A1AA] text-sm">Target Multiplier</label>
                <span className="text-[#007AFF] font-bold">{targetMultiplier.toFixed(2)}x</span>
              </div>
              <Slider
                value={[targetMultiplier]}
                onValueChange={(v) => setTargetMultiplier(v[0])}
                min={1.01}
                max={100}
                step={0.01}
                disabled={loading}
                className="my-4"
              />
              <div className="flex gap-2">
                {[1.5, 2.0, 5.0, 10.0, 50.0].map(mult => (
                  <Button
                    key={mult}
                    variant="outline"
                    size="sm"
                    onClick={() => setTargetMultiplier(mult)}
                    disabled={loading}
                    className="flex-1 border-white/10 text-white/60 hover:text-white text-xs"
                  >
                    {mult}x
                  </Button>
                ))}
              </div>
            </div>

            {/* Win Chance & Payout */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#007AFF]/10 to-transparent border border-[#007AFF]/20">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-[#A1A1AA] text-sm">Win Chance</p>
                  <p className="text-2xl font-bold text-[#007AFF]">{winChance}%</p>
                </div>
                <div className="text-right">
                  <p className="text-[#A1A1AA] text-sm">Potential Win</p>
                  <p className="text-2xl font-bold text-[#00E701]">
                    ₹{(betAmount * targetMultiplier).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Play Button */}
            <Button
              onClick={playGame}
              disabled={loading || betAmount <= 0 || betAmount > (user?.balance || 0)}
              className="w-full h-14 bg-[#007AFF] hover:bg-[#0066DD] text-white font-bold text-lg"
              data-testid="play-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Rolling...
                </div>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Bet ₹{betAmount} for {targetMultiplier}x
                </>
              )}
            </Button>

            {/* History */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <History className="w-4 h-4" /> Recent
              </h3>
              <div className="flex flex-wrap gap-2">
                {history.map((h, i) => (
                  <span 
                    key={i}
                    className={`px-2 py-1 rounded text-xs font-mono ${
                      h.won ? 'bg-[#00E701]/20 text-[#00E701]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'
                    }`}
                  >
                    {h.result}x
                  </span>
                ))}
                {history.length === 0 && (
                  <span className="text-[#52525B] text-sm">No games yet</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
